import { MoreVertical, Pencil, Plus, Server, Trash } from "lucide-react";
import React, { useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { useNavigate } from "react-router";
import { ScopeGate } from "src/components/ScopeGate";
import { Button } from "src/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { UIConfirmationDialog } from "src/components/ui/UIConfirmationDialog";
import { usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import { useMcpOAuthCallback } from "src/pages/SettingsPage/routes/mcp-server-utils";

import { CustomMcpServersSectionDeleteMutation } from "./__generated__/CustomMcpServersSectionDeleteMutation.graphql";
import { CustomMcpServersSectionQuery } from "./__generated__/CustomMcpServersSectionQuery.graphql";

const customMcpServersQuery = graphql`
  query CustomMcpServersSectionQuery {
    customMcpServers {
      id
      name
      description
      url
      authType
      isActive
      discoveredTools
      toolCount
      resourceCount
      createdAt
    }
  }
`;

const deleteServerMutation = graphql`
  mutation CustomMcpServersSectionDeleteMutation($id: GlobalID!) {
    deleteCustomMcpServer(id: $id) {
      server {
        id
      }
      error
    }
  }
`;

export default function CustomMcpServersSection() {
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");
  const { toast } = useToast();
  const navigate = useNavigate();

  const [fetchKey, setFetchKey] = useState(0);
  const data = useLazyLoadQuery<CustomMcpServersSectionQuery>(
    customMcpServersQuery,
    {},
    { fetchPolicy: "network-only", fetchKey },
  );

  const [commitDelete, isDeleting] = useMutation<CustomMcpServersSectionDeleteMutation>(deleteServerMutation);

  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; serverId: string | null; serverName: string }>({
    isOpen: false,
    serverId: null,
    serverName: "",
  });
  const servers = data.customMcpServers ?? [];

  useMcpOAuthCallback(toast);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  const handleDelete = useCallback(() => {
    if (!deleteConfirm.serverId) return;
    commitDelete({
      variables: { id: deleteConfirm.serverId },
      onCompleted: () => {
        toast({ title: "Deleted", description: `${deleteConfirm.serverName} has been removed.` });
        setDeleteConfirm({ isOpen: false, serverId: null, serverName: "" });
        refetch();
      },
      onError: (error) => {
        toast({ title: "Error", description: error.message, variant: "error" });
      },
    });
  }, [deleteConfirm, commitDelete, toast, refetch]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        {servers.length < 5 && (
          <ScopeGate scope="write:integrations">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate("/settings/integrations/mcp-servers/new")}
            >
              <Plus className="size-3.5 mr-1.5" />
              Add Server
            </Button>
          </ScopeGate>
        )}
      </div>

      {servers.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <Server className="mx-auto size-8 text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">No custom MCP servers configured</p>
        </div>
      ) : (
        <div className="space-y-2">
          {servers.map((server) => (
            <div
              key={server.id}
              className="flex items-center justify-between rounded-lg border border-border bg-card p-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={`size-2 rounded-full flex-shrink-0 ${server.isActive ? "bg-green-500" : "bg-yellow-500"}`}
                />
                <div className="min-w-0">
                  <span className="text-sm font-medium text-foreground truncate">{server.name}</span>
                  {server.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[400px]">
                      {server.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                    <span className="truncate max-w-[300px]">{server.url}</span>
                    <span>·</span>
                    <span>{server.toolCount} tool{server.toolCount !== 1 ? "s" : ""}</span>
                    {server.resourceCount > 0 && (
                      <>
                        <span>·</span>
                        <span>{server.resourceCount} resource{server.resourceCount !== 1 ? "s" : ""}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {canModify && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="size-8 p-0">
                      <MoreVertical className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => navigate(`/settings/integrations/mcp-servers/${server.id}/edit`)}
                    >
                      <Pencil className="size-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() =>
                        setDeleteConfirm({ isOpen: true, serverId: server.id, serverName: server.name })
                      }
                    >
                      <Trash className="size-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <UIConfirmationDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, serverId: null, serverName: "" })}
        onConfirm={handleDelete}
        title="Delete MCP Server"
        description={`Are you sure you want to delete "${deleteConfirm.serverName}"? The agent will no longer have access to its tools.`}
        confirmText="Delete"
        variant="destructive"
        isLoading={isDeleting}
      />
    </div>
  );
}
